import SyncActiveToast from 'components/SyncActiveToast';
import { AppContext } from './AppContextProvider';
import { getSyncOperationFromJobType, SYNC_OPERATION_META, type SyncOperationKind } from 'pages/RecognitionPages/Recognitions/utils';
import {
  createContext,
  type FC,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useJobQuery } from 'services/Jobs/hooks/useQuery';
import type { IJobProps } from 'services/Jobs/types';

const SUCCESS_LINGER_MS = 4000;

const countDevices = (json?: string | null): number => {
  if (!json) return 0;
  try {
    const parsed = JSON.parse(json);
    return Array.isArray(parsed) ? parsed.length : 0;
  } catch {
    return 0;
  }
};

const FACES_ROUTE = '/app/recognitions';

const SYNC_KEY = 'alertai_sync_active';
const SYNC_TTL = 15 * 60 * 1000;

interface StoredSync {
  jobId: number;
  entityId: number;
  personName: string;
  progress: number;
  message: string;
  operation?: SyncOperationKind;
  t: number;
}

const syncStore = {
  save(
    jobId: number,
    entityId: number,
    personName: string,
    progress: number,
    message: string,
    operation: SyncOperationKind,
  ) {
    try {
      localStorage.setItem(
        SYNC_KEY,
        JSON.stringify({
          jobId,
          entityId,
          personName,
          progress,
          message,
          operation,
          t: Date.now(),
        }),
      );
    } catch {
      /* armazenamento indisponível */
    }
  },
  load(): Omit<StoredSync, 't' | 'operation'> & { operation: SyncOperationKind } | null {
    try {
      const raw = localStorage.getItem(SYNC_KEY);
      if (!raw) return null;
      const d = JSON.parse(raw) as StoredSync;
      if (Date.now() - d.t > SYNC_TTL) {
        localStorage.removeItem(SYNC_KEY);
        return null;
      }
      return {
        jobId: d.jobId,
        entityId: d.entityId,
        personName: d.personName,
        progress: d.progress,
        message: d.message,
        // Sessões antigas gravadas antes do campo operation existir caem em 'create' por padrão.
        operation: d.operation ?? 'create',
      };
    } catch {
      return null;
    }
  },
  clear() {
    try {
      localStorage.removeItem(SYNC_KEY);
    } catch {
      /* armazenamento indisponível */
    }
  },
};

export interface SyncJob {
  jobId: number;
  entityId: number;
  personName: string;
  progress: number;
  message: string;
  operation: SyncOperationKind;
}

export interface SyncContextValue {
  job: SyncJob | null;
  /** Job cru do poll (services/Jobs), pra quem precisar de logData/resultData/status/jobType (ex: FaceSyncRunner). */
  jobData: IJobProps | undefined;
  isToastVisible: boolean;
  successFlash: boolean;
  entityJobMap: Record<number, number>;
  /** Nome de quem está sendo salvo antes do job existir (upload da imagem ainda em voo). */
  preparingName: string | null;
  preparingOperation: SyncOperationKind | null;
  startPreparing: (personName: string, operation: SyncOperationKind) => void;
  stopPreparing: () => void;
  setSyncJob: (
    jobId: number,
    entityId: number,
    personName: string,
    progress: number,
    message: string,
    operation: SyncOperationKind,
  ) => void;
  clearSyncJob: () => void;
  showToast: () => void;
  hideToast: () => void;
  setAnimating: (v: boolean) => void;
  registerViewCallback: (cb: (() => void) | null) => void;
  registerCloseCallback: (cb: (() => void) | null) => void;
  getJobIdForEntity: (entityId: number) => number | undefined;
}

const SyncContext = createContext<SyncContextValue | null>(null);

export const useSyncContext = (): SyncContextValue => {
  const ctx = useContext(SyncContext);
  if (!ctx)
    throw new Error('useSyncContext must be used within SyncJobProvider');
  return ctx;
};

export const SyncJobProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [job, setJob] = useState<SyncJob | null>(null);
  const [toastVisible, setToastVisible] = useState(false);
  const [successFlash, setSuccessFlash] = useState(false);
  const [entityJobMap, setEntityJobMap] = useState<Record<number, number>>({});
  const [preparingName, setPreparingName] = useState<string | null>(null);
  const [preparingOperation, setPreparingOperation] = useState<SyncOperationKind | null>(null);

  const animatingRef = useRef(false);
  const viewCallbackRef = useRef<(() => void) | null>(null);
  const closeCallbackRef = useRef<(() => void) | null>(null);

  const { showSnackbar } = useContext(AppContext);

  // Fica no Provider (topo do app), não num componente de página, de propósito: o poll precisa
  // continuar mesmo que o usuário saia da página de Faces enquanto o job roda em background (B17).
  const jobId = job?.jobId ?? null;
  const { data: jobData } = useJobQuery(jobId);

  useEffect(() => {
    if (!jobData) return;
    setJob((p) => (p ? { ...p, progress: jobData.progress, message: jobData.message ?? '' } : p));
  }, [jobData]);

  useEffect(() => {
    if (jobData?.status !== 'Completed') return;
    setSuccessFlash(true);
    syncStore.clear();
    const timer = setTimeout(() => setSuccessFlash(false), SUCCESS_LINGER_MS);
    return () => clearTimeout(timer);
  }, [jobData?.status]);

  // Job concluído sem dispositivo vinculado: não há dialog/toast de sync fazendo sentido, troca
  // pelo snackbar padrão de confirmação, igual o resto do app usa pra um salvamento comum.
  useEffect(() => {
    if (!jobData || jobData.status !== 'Completed') return;
    const devicesCount = countDevices(jobData.logData ?? jobData.resultData);
    if (devicesCount > 0) return;
    const operation = job?.operation ?? getSyncOperationFromJobType(jobData.jobType);
    showSnackbar(SYNC_OPERATION_META[operation].doneMessage, 'success');
    setJob(null);
    setToastVisible(false);
    setSuccessFlash(false);
  }, [jobData, job?.operation, showSnackbar]);

  useEffect(() => {
    if (job)
      syncStore.save(
        job.jobId,
        job.entityId,
        job.personName,
        job.progress,
        job.message,
        job.operation,
      );
  }, [job]);

  // Restaura mesmo fora da tela de faces — o provider fica no topo do app, não na página.
  useEffect(() => {
    const saved = syncStore.load();
    if (!saved) return;
    setJob({
      jobId: saved.jobId,
      entityId: saved.entityId,
      personName: saved.personName,
      progress: saved.progress,
      message: saved.message,
      operation: saved.operation,
    });
    setEntityJobMap((p) => ({ ...p, [saved.entityId]: saved.jobId }));
    setToastVisible(true);
  }, []);

  useEffect(() => {
    if (!job) return undefined;
    const onUnload = (e: BeforeUnloadEvent) => {
      if (!animatingRef.current) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onUnload);
    return () => window.removeEventListener('beforeunload', onUnload);
  }, [job]);

  const navigate = useNavigate();

  const clearSyncJob = useCallback(() => {
    animatingRef.current = false;
    setJob(null);
    setToastVisible(false);
    setSuccessFlash(false);
    syncStore.clear();
  }, []);

  const handleView = useCallback(() => {
    if (viewCallbackRef.current) {
      viewCallbackRef.current();
    } else {
      // Usuário está em uma página diferente — navega para faces para que o componente registre seu callback
      navigate(FACES_ROUTE);
    }
  }, [navigate]);

  const handleClose = useCallback(() => {
    if (closeCallbackRef.current) {
      closeCallbackRef.current();
    } else {
      // Nenhum handler montado no nível da página (ex: usuário está em outra página)
      clearSyncJob();
    }
  }, [clearSyncJob]);

  const startPreparing = useCallback(
    (name: string, operation: SyncOperationKind) => {
      setPreparingName(name);
      setPreparingOperation(operation);
    },
    [],
  );
  const stopPreparing = useCallback(() => {
    setPreparingName(null);
    setPreparingOperation(null);
  }, []);

  const setSyncJob = useCallback(
    (
      jobId: number,
      entityId: number,
      name: string,
      progress: number,
      message: string,
      operation: SyncOperationKind,
    ) => {
      setJob({ jobId, entityId, personName: name, progress, message, operation });
      setEntityJobMap((p) => ({ ...p, [entityId]: jobId }));
      setPreparingName(null);
      setPreparingOperation(null);
    },
    [],
  );

  const showToast = useCallback(() => setToastVisible(true), []);
  const hideToast = useCallback(() => setToastVisible(false), []);
  const setAnimating = useCallback((v: boolean) => {
    animatingRef.current = v;
  }, []);
  const registerViewCallback = useCallback((cb: (() => void) | null) => {
    viewCallbackRef.current = cb;
  }, []);
  const registerCloseCallback = useCallback((cb: (() => void) | null) => {
    closeCallbackRef.current = cb;
  }, []);
  const getJobIdForEntity = useCallback(
    (entityId: number) => entityJobMap[entityId],
    [entityJobMap],
  );

  const value: SyncContextValue = useMemo(
    () => ({
      job,
      jobData,
      isToastVisible: toastVisible,
      successFlash,
      entityJobMap,
      preparingName,
      preparingOperation,
      startPreparing,
      stopPreparing,
      setSyncJob,
      clearSyncJob,
      showToast,
      hideToast,
      setAnimating,
      registerViewCallback,
      registerCloseCallback,
      getJobIdForEntity,
    }),
    [
      job,
      jobData,
      toastVisible,
      successFlash,
      entityJobMap,
      preparingName,
      preparingOperation,
      startPreparing,
      stopPreparing,
      setSyncJob,
      clearSyncJob,
      showToast,
      hideToast,
      setAnimating,
      registerViewCallback,
      registerCloseCallback,
      getJobIdForEntity,
    ],
  );

  return (
    <SyncContext.Provider value={value}>
      {children}
      {job && (
        <SyncActiveToast
          open={toastVisible}
          personName={job.personName}
          progress={job.progress}
          statusMessage={job.message}
          success={successFlash}
          successLingerMs={4000}
          operation={job.operation}
          onView={handleView}
          onClose={handleClose}
        />
      )}
    </SyncContext.Provider>
  );
};
